import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle, XCircle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface Referral {
  id: string;
  referrer_id: string;
  referred_user_id: string;
  status: string;
  signup_date: string;
  purchase_date: string | null;
  purchase_amount: number;
  commission_earned: number;
  referrer?: { username: string };
  referred_user?: { username: string };
}

interface Payout {
  id: string;
  user_id: string;
  amount: number;
  status: string;
  payment_method: string | null;
  payment_details: any;
  requested_at: string;
  notes: string | null;
  profiles?: { username: string };
}

const AdminReferrals = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);

  useEffect(() => {
    checkAdminAndLoadData();
  }, []);

  const checkAdminAndLoadData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .single();

      if (!roleData) {
        navigate("/dashboard");
        return;
      }

      setIsAdmin(true);
      await loadData();
    } catch (error) {
      console.error(error);
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const { data: referralsData } = await supabase
        .from("referrals")
        .select("*")
        .order("created_at", { ascending: false });

      // Fetch profile data separately
      if (referralsData && referralsData.length > 0) {
        const referrerIds = [...new Set(referralsData.map(r => r.referrer_id))];
        const referredIds = [...new Set(referralsData.map(r => r.referred_user_id))];
        const allIds = [...new Set([...referrerIds, ...referredIds])];

        const { data: profilesData } = await supabase
          .from("profiles")
          .select("id, username")
          .in("id", allIds);

        const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);

        const enrichedReferrals = referralsData.map(r => ({
          ...r,
          referrer: profilesMap.get(r.referrer_id),
          referred_user: profilesMap.get(r.referred_user_id),
        }));

        setReferrals(enrichedReferrals as any);
      } else {
        setReferrals([]);
      }

      const { data: payoutsData } = await supabase
        .from("payouts")
        .select("*")
        .order("requested_at", { ascending: false });

      // Fetch profile data for payouts
      if (payoutsData && payoutsData.length > 0) {
        const userIds = [...new Set(payoutsData.map(p => p.user_id))];
        const { data: profilesData } = await supabase
          .from("profiles")
          .select("id, username")
          .in("id", userIds);

        const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);

        const enrichedPayouts = payoutsData.map(p => ({
          ...p,
          profiles: profilesMap.get(p.user_id),
        }));

        setPayouts(enrichedPayouts as any);
      } else {
        setPayouts([]);
      }
    } catch (error: any) {
      toast.error("Failed to load data");
      console.error(error);
    }
  };

  const updateReferralStatus = async (id: string, status: string) => {
    try {
      await supabase
        .from("referrals")
        .update({ status })
        .eq("id", id);

      toast.success("Referral status updated");
      loadData();
    } catch (error) {
      toast.error("Failed to update status");
      console.error(error);
    }
  };

  const updatePayoutStatus = async (id: string, status: string) => {
    try {
      const update: any = { status };
      if (status === "approved") {
        update.approved_at = new Date().toISOString();
      } else if (status === "paid") {
        update.paid_at = new Date().toISOString();
      }

      await supabase
        .from("payouts")
        .update(update)
        .eq("id", id);

      toast.success("Payout status updated");
      loadData();
    } catch (error) {
      toast.error("Failed to update payout");
      console.error(error);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      pending: "secondary",
      active: "default",
      completed: "default",
      approved: "default",
      paid: "default",
      rejected: "destructive",
    };
    return <Badge variant={variants[status] || "secondary"}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-4xl font-bold">Referrals Management</h1>
        </div>

        <Tabs defaultValue="referrals">
          <TabsList>
            <TabsTrigger value="referrals">All Referrals</TabsTrigger>
            <TabsTrigger value="payouts">Payout Requests</TabsTrigger>
          </TabsList>

          <TabsContent value="referrals">
            <Card>
              <CardHeader>
                <CardTitle>All Referrals</CardTitle>
                <CardDescription>Manage referral tracking and commissions</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Referrer</TableHead>
                      <TableHead>Referred User</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Signup Date</TableHead>
                      <TableHead>Purchase</TableHead>
                      <TableHead>Commission</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referrals.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground">
                          No referrals yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      referrals.map((referral) => (
                        <TableRow key={referral.id}>
                          <TableCell>{referral.referrer?.username || "Unknown"}</TableCell>
                          <TableCell>{referral.referred_user?.username || "Unknown"}</TableCell>
                          <TableCell>{getStatusBadge(referral.status)}</TableCell>
                          <TableCell>
                            {new Date(referral.signup_date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {referral.purchase_amount
                              ? `$${(referral.purchase_amount / 100).toFixed(2)}`
                              : "-"}
                          </TableCell>
                          <TableCell>
                            ${(referral.commission_earned / 100).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              {referral.status !== "active" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateReferralStatus(referral.id, "active")}
                                >
                                  Activate
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payouts">
            <Card>
              <CardHeader>
                <CardTitle>Payout Requests</CardTitle>
                <CardDescription>Approve and manage payout requests</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead>Requested</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payouts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground">
                          No payout requests yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      payouts.map((payout) => (
                        <TableRow key={payout.id}>
                          <TableCell>{payout.profiles?.username || "Unknown"}</TableCell>
                          <TableCell>${(payout.amount / 100).toFixed(2)}</TableCell>
                          <TableCell>{getStatusBadge(payout.status)}</TableCell>
                          <TableCell>{payout.payment_method || "-"}</TableCell>
                          <TableCell className="max-w-xs truncate">
                            {payout.payment_details?.details || "-"}
                          </TableCell>
                          <TableCell>
                            {new Date(payout.requested_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              {payout.status === "pending" && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => updatePayoutStatus(payout.id, "approved")}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => updatePayoutStatus(payout.id, "rejected")}
                                  >
                                    <XCircle className="w-4 h-4 mr-1" />
                                    Reject
                                  </Button>
                                </>
                              )}
                              {payout.status === "approved" && (
                                <Button
                                  size="sm"
                                  onClick={() => updatePayoutStatus(payout.id, "paid")}
                                >
                                  Mark as Paid
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminReferrals;
